'use client';

import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';
import { useCallback, useState } from 'react';
import { HStack, VStack } from '@terra/ui-styled-system';
import { buildMaskedValuePreview } from '@terra/shared-utils';
import { IconButton } from '../../../components/icon-button';
import { EyeIcon, Tick01Icon, Copy01Icon, ViewOffIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';

const MASKED_VALUE_PREVIEW = buildMaskedValuePreview();

export const isCopyablePlaintextValue = (value: string): boolean =>
  Boolean(value) &&
  value !== '-' &&
  value !== MASKED_VALUE_PREVIEW &&
  !/^[•*]+$/.test(value);

export const CopyButton = ({ value }: { value: string }) => {
  const t = useTranslations('designSystem.credentials.card');
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  }, [value]);

  return (
    <IconButton
      variant="ghost"
      size="xs"
      aria-label={t('aria.copy')}
      onClick={handleCopy}
      color={copied ? 'fg.success' : undefined}
    >
      {copied ? <Tick01Icon size={14} /> : <Copy01Icon size={14} />}
    </IconButton>
  );
};

type ReadOnlyFieldProps = {
  label: string;
  value: string;
  helperText?: string;
  multiline?: boolean;
  copyable?: boolean;
  sensitive?: boolean;
};

export const ReadOnlyField = ({
  label,
  value,
  helperText,
  multiline,
  copyable = true,
  sensitive,
}: ReadOnlyFieldProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const [isVisible, setIsVisible] = useState(false);
  const canReveal = Boolean(sensitive && value && value !== '-');
  const displayValue = canReveal && !isVisible ? '••••••••••' : value;

  return (
    <VStack alignItems="stretch" gap={1.5} w="full">
      <LabelDefault as="span" color="fg.neutral.default">
        {label}
      </LabelDefault>
      <HStack
        gap={2}
        alignItems={multiline ? 'flex-start' : 'center'}
        px={2}
        py={2}
        borderWidth="1px"
        borderColor="border.neutral.disabled"
        borderRadius="sm"
        bg="bg.neutral.subtle"
      >
        <BodyDefault
          color="fg.neutral.subtle"
          flex={1}
          minW={0}
          truncate={!multiline}
          whiteSpace={multiline ? 'pre-wrap' : undefined}
          wordBreak={multiline ? 'break-word' : undefined}
        >
          {displayValue}
        </BodyDefault>
        {canReveal ? (
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={isVisible ? t('aria.hideValue') : t('aria.showValue')}
            onClick={() => setIsVisible((previous) => !previous)}
          >
            {isVisible ? <ViewOffIcon size={14} /> : <EyeIcon size={14} />}
          </IconButton>
        ) : null}
        {copyable && isCopyablePlaintextValue(value) ? (
          <CopyButton value={value} />
        ) : null}
      </HStack>
      {helperText ? (
        <MetaDefault color="fg.neutral.subtle">{helperText}</MetaDefault>
      ) : null}
    </VStack>
  );
};
